import { z } from 'zod';

/**
 * HardwareSpec — canonical normalized spec object stored in
 * `market_comp.raw_payload` (and later `staging_listing.normalized_spec`).
 *
 * Discriminated on `unit_type`:
 *   - 'complete_server'  → full chassis with CPU/RAM + optional GPUs/storage
 *   - 'gpu_only'         → GPU-only listing (loose card, no host)
 *   - 'component'        → standalone component (CPU, RAM kit, NIC, etc.)
 *
 * Every crawler (eBay LLM extractor, ServerMonkey, CDW) must produce a
 * HardwareSpec; the outlier validator + SKU matcher consume it
 * downstream. Keep fields stable — schema changes ripple into prompt
 * templates and historical raw_payload.
 */

const formFactorSchema = z.enum(['1U', '2U', '3U', '4U', '5U', 'tower', 'blade', 'other']);

const storageDriveSchema = z.object({
  drive_type: z.enum(['hdd', 'ssd', 'nvme']),
  capacity_gb: z.number().int().positive(),
  count: z.number().int().positive().default(1),
  interface: z.enum(['sata', 'sas', 'nvme', 'unknown']).optional(),
});

const networkingSchema = z.object({
  ports: z.array(
    z.object({
      speed_gbps: z.number().positive(),
      count: z.number().int().positive(),
      media: z.enum(['rj45', 'sfp', 'sfp_plus', 'qsfp', 'qsfp_plus', 'unknown']).optional(),
    }),
  ),
});

const completeServerSchema = z.object({
  unit_type: z.literal('complete_server'),
  manufacturer: z.string().nullable(),
  model: z.string().nullable(),
  form_factor: formFactorSchema.nullable(),
  cpu_model: z.string().nullable(),
  cpu_count: z.number().int().positive().nullable(),
  cpu_cores_total: z.number().int().positive().nullable(),
  ram_gb: z.number().int().positive().nullable(),
  ram_speed_mts: z.number().int().positive().nullable(),
  gpu_model: z.string().nullable(),
  gpu_count: z.number().int().nonnegative().nullable(),
  gpu_memory_gb: z.number().int().positive().nullable(),
  storage_included: z.boolean().nullable(),
  // LLM sometimes returns storage_drives as a count (number) or as an array
  // with objects missing required fields. Coerce both to null rather than
  // failing the whole record — storage detail is non-critical for FMV.
  storage_drives: z.preprocess(
    (v) => {
      if (v === null || v === undefined) return null;
      if (typeof v === 'number') return null; // LLM returned a count — drop it
      if (!Array.isArray(v)) return null;
      // Filter to only items that pass storageDriveSchema; drop the rest
      const valid = v.filter((item) => storageDriveSchema.safeParse(item).success);
      return valid.length > 0 ? valid : null;
    },
    z.array(storageDriveSchema).nullable(),
  ),
  storage_total_capacity_gb: z.number().int().positive().nullable(),
  // LLM sometimes returns networking as a plain string — coerce to null
  networking: z.preprocess(
    (v) => (typeof v === 'string' || (v !== null && typeof v !== 'object') ? null : v),
    networkingSchema.nullable(),
  ),
  power_supply_count: z.number().int().positive().nullable(),
  power_supply_watts: z.number().int().positive().nullable(),
  extended: z.record(z.unknown()).default({}),
});

const gpuOnlySchema = z.object({
  unit_type: z.literal('gpu_only'),
  manufacturer: z.string().nullable(),
  model: z.string(),
  gpu_memory_gb: z.number().int().positive().nullable(),
  form_factor: z.enum(['pcie', 'sxm', 'sxm2', 'sxm4', 'sxm5', 'oam', 'unknown']).nullable(),
  count: z.number().int().positive().default(1),
  extended: z.record(z.unknown()).default({}),
});

const componentSchema = z.object({
  unit_type: z.literal('component'),
  // SIL-985: 'dpu' and 'switch' added — see hardware-spec.ts module doc /
  // normalizer.ts's COMPONENT json_schema for why.
  component_kind: z.enum(['cpu', 'ram', 'nic', 'dpu', 'switch', 'hba', 'psu', 'storage', 'other']),
  manufacturer: z.string().nullable(),
  model: z.string(),
  capacity_gb: z.number().int().positive().nullable(),
  count: z.number().int().positive().default(1),
  // SIL-985 — networking port signals (nic/dpu/switch only). Read by
  // sku-matcher.ts's family+speed+port fallback rung.
  port_speed_gbps: z.number().int().positive().nullable(),
  port_count: z.number().int().positive().nullable(),
  extended: z.record(z.unknown()).default({}),
});

export const hardwareSpecSchema = z.discriminatedUnion('unit_type', [
  completeServerSchema,
  gpuOnlySchema,
  componentSchema,
]);

export type HardwareSpec = z.infer<typeof hardwareSpecSchema>;
export type CompleteServerSpec = z.infer<typeof completeServerSchema>;
export type GpuOnlySpec = z.infer<typeof gpuOnlySchema>;
export type ComponentSpec = z.infer<typeof componentSchema>;
